import { Album, FolderOperationResult } from "../../../../types"

// Recalculate isEmpty flags for all albums
const recalculateIsEmpty = (albums: Album[]): Album[] => {
  const updatedAlbums = albums.map(album => ({ ...album }))
  
  // Function to check if an album (and its children) contain any photos
  const hasPhotosInSubtree = (albumId: string, visited = new Set<string>()): boolean => {
    if (visited.has(albumId)) return false // Prevent infinite loops
    visited.add(albumId)
    
    const album = updatedAlbums.find(a => a.uuid === albumId)
    if (!album) return false
    
    // If this is a real album (from backend), trust its isEmpty value
    if (!album.uuid.startsWith('temp_')) {
      return !album.isEmpty // Backend knows if it has photos or not
    }
    
    // Check if any children have photos
    const children = updatedAlbums.filter(a => a.parentId === albumId)
    return children.some(child => hasPhotosInSubtree(child.uuid, visited))
  }
  
  // Update isEmpty flags
  for (const album of updatedAlbums) {
    if (album.uuid.startsWith('temp_')) {
      // ONLY touch temp albums - recalculate based on contents
      album.isEmpty = !hasPhotosInSubtree(album.uuid)
    }
    // DO NOT touch real albums - backend is the source of truth!
  }
  
  return updatedAlbums
}

export function createEmptyAlbum(
  albums: Album[], 
  name: string, 
  parentId?: string, 
  photographerId?: string,
  memoirId?: string
): FolderOperationResult {
  if (!photographerId) {
    return { success: false, error: 'User ID is required' }
  }
  // SOL-204: an album belongs to a Memoir from the moment it exists, even as
  // a local temp_ album, because its first upload is what creates it on the
  // server and the server refuses a new album without one.
  if (!memoirId) {
    return { success: false, error: 'Albums can only be created inside a Memoir' }
  }

  const newAlbum: Album = {
    uuid: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
    name: name.trim(),
    photographerId,
    createdAt: new Date().toISOString(),
    parentId,
    memoirId,
    isEmpty: true
  }

  const updatedAlbums = [...albums, newAlbum]
  return { success: true, albums: recalculateIsEmpty(updatedAlbums) }
}

export function renameAlbum(
  albums: Album[], 
  albumId: string, 
  newName: string
): FolderOperationResult {
  const trimmedName = newName.trim()
  if (!trimmedName) {
    return { success: false, error: 'Album name cannot be empty' }
  }

  const updatedAlbums = albums.map(album => 
    album.uuid === albumId 
      ? { ...album, name: trimmedName }
      : album
  )

  if (updatedAlbums.length === albums.length && !updatedAlbums.some(a => a.uuid === albumId)) {
    return { success: false, error: 'Album not found' }
  }

  return { success: true, albums: recalculateIsEmpty(updatedAlbums) }
}

export function deleteEmptyAlbum(
  albums: Album[], 
  albumId: string
): FolderOperationResult {
  const album = albums.find(a => a.uuid === albumId)
  if (!album) {
    return { success: false, error: 'Album not found' }
  }

  if (!album.isEmpty) {
    return { success: false, error: 'Cannot delete album with photos' }
  }

  // Check if it has children
  const hasChildren = albums.some(a => a.parentId === albumId)
  if (hasChildren) {
    return { success: false, error: 'Cannot delete album that contains other albums' }
  }

  const updatedAlbums = albums.filter(a => a.uuid !== albumId)
  return { success: true, albums: recalculateIsEmpty(updatedAlbums) }
}

export function changeDateAlbum(
  albums: Album[], 
  albumId: string, 
  newDate: string
): FolderOperationResult {
  // Validate date
  const dateObj = new Date(newDate)
  if (isNaN(dateObj.getTime())) {
    return { success: false, error: 'Invalid date format' }
  }

  const updatedAlbums = albums.map(album => 
    album.uuid === albumId 
      ? { ...album, createdAt: newDate }
      : album
  )

  if (updatedAlbums.length === albums.length && !updatedAlbums.some(a => a.uuid === albumId)) {
    return { success: false, error: 'Album not found' }
  }

  return { success: true, albums: recalculateIsEmpty(updatedAlbums) }
}

export function moveAlbum(
  albums: Album[], 
  albumId: string, 
  targetParentId?: string
): FolderOperationResult {
  const album = albums.find(a => a.uuid === albumId)
  if (!album) {
    return { success: false, error: 'Album not found' }
  }

  // If already in the target location, silently succeed (no-op)
  if (album.parentId === targetParentId) {
    return { success: true, albums: recalculateIsEmpty(albums) }
  }

  // Prevent moving into self or descendants
  if (targetParentId && targetParentId === albumId) {
    // Trying to move album into itself
    return { success: true, albums: recalculateIsEmpty(albums) } // Silently ignore
  }
  
  if (targetParentId) {
    const isDescendant = (parentId: string, checkId: string): boolean => {
      const parent = albums.find(a => a.uuid === parentId)
      if (!parent || !parent.parentId) return false
      if (parent.parentId === checkId) return true
      return isDescendant(parent.parentId, checkId)
    }

    if (isDescendant(targetParentId, albumId)) {
      return { success: true, albums: recalculateIsEmpty(albums) } // Silently ignore instead of error
    }
  }

  const updatedAlbums = albums.map(a => 
    a.uuid === albumId 
      ? { ...a, parentId: targetParentId }
      : a
  )

  return { success: true, albums: recalculateIsEmpty(updatedAlbums) }
}

/**
 * KISS: Get albums in folder (or root if parentId is undefined)
 */
export function getAlbumsInFolder(
  albums: Album[], 
  parentId: string | undefined
): Album[] {
  return albums.filter(album => album.parentId === parentId)
}

/**
 * KISS: Get breadcrumb path for album
 */
export function getBreadcrumbPath(
  albums: Album[], 
  currentAlbumId: string | undefined
): Array<{ id: string; name: string }> {
  if (!currentAlbumId) return []
  
  const path: Array<{ id: string; name: string }> = []
  let current: Album | undefined = albums.find(a => a.uuid === currentAlbumId)
  
  while (current) {
    path.unshift({ id: current.uuid, name: current.name })
    
    if (!current.parentId) {
      break
    }
    
    current = albums.find(a => a.uuid === current!.parentId)
  }
  
  return path
} 